import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ParentLayout from '../components/ParentLayout';
import { Send, CheckCircle, Users, GraduationCap, Calendar, RefreshCw, ChevronDown, FileText, AlertCircle, Eye } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAccountabilityPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [weekOf, setWeekOf] = useState(getNextMonday());
  const [weekStatus, setWeekStatus] = useState({});
  const [recentReports, setRecentReports] = useState([]);
  const [showReports, setShowReports] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  function getNextMonday() {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? 1 : (day === 1 ? 0 : 8 - day);
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    return monday.toISOString().split('T')[0];
  }

  const fetchTeams = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/accountability/teams`, { withCredentials: true });
      setTeams(res.data.teams || []);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  }, []);

  const fetchWeekStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/accountability/week-status?week_of=${weekOf}`, { withCredentials: true });
      const statusMap = {};
      (res.data.teams || []).forEach(t => {
        statusMap[t.team_id] = t;
      });
      setWeekStatus(statusMap);
    } catch (err) {
      console.error('Failed to fetch week status:', err);
    }
  }, [weekOf]);

  const fetchRecentReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/accountability/reports?week_of=${weekOf}`, { withCredentials: true });
      setRecentReports(res.data.reports || []);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    }
  }, [weekOf]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTeams(), fetchWeekStatus(), fetchRecentReports()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTeams, fetchWeekStatus, fetchRecentReports]);

  const toggleTeam = (teamId) => {
    setSelectedTeams(prev => 
      prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const selectAllTeams = () => {
    if (selectedTeams.length === teams.length) {
      setSelectedTeams([]);
    } else {
      setSelectedTeams(teams.map(t => t.team_id));
    }
  };

  const handleSend = async () => {
    if (selectedTeams.length === 0) {
      alert('Please select at least one team');
      return;
    }

    setSending(true);
    setSendResult(null);

    try {
      const res = await axios.post(`${API_URL}/api/admin/accountability/send`, {
        team_ids: selectedTeams,
        week_of: weekOf
      }, { withCredentials: true });

      setSendResult({
        success: true,
        message: res.data.message,
        emails_sent: res.data.emails_sent
      });

      // Refresh data
      await fetchWeekStatus();
      setSelectedTeams([]);
    } catch (err) {
      setSendResult({
        success: false,
        message: err.response?.data?.detail || 'Failed to send reports'
      });
    } finally {
      setSending(false);
    }
  };

  const getTeamStats = () => {
    const selected = teams.filter(t => selectedTeams.includes(t.team_id));
    const totalPlayers = selected.reduce((sum, t) => sum + (t.player_count || 0), 0);
    const totalTeachers = selected.reduce((sum, t) => sum + (t.teacher_count || 0), 0);
    return { totalPlayers, totalTeachers };
  };

  const stats = getTeamStats();

  const getBehaviorColor = (score) => {
    if (score >= 4) return 'text-green-600 bg-green-100';
    if (score === 3) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getAcademicBadge = (status) => {
    if (status === 'on_track') {
      return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">On Track</span>;
    }
    return <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">Needs Improvement</span>;
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c41e3a] mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[#fae36c] text-sm font-bold tracking-widest uppercase mb-1">ACCOUNTABILITY</p>
          <h1 className="text-3xl md:text-4xl font-display text-[#32425c] uppercase tracking-wide">
            WEEKLY TEACHER REPORTS
          </h1>
          <p className="text-gray-600 mt-2">
            Send weekly check-in requests to teachers for student-athletes
          </p>
        </div>

        {/* Send Result Alert */}
        {sendResult && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            sendResult.success 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            {sendResult.success ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            )}
            <p className={sendResult.success ? 'text-green-700' : 'text-red-700'}>
              {sendResult.message}
            </p>
            <button 
              onClick={() => setSendResult(null)}
              className="ml-auto text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Send Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Week Selection */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-[#32425c] flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Week Selection
                </h2>
                <button
                  onClick={() => { fetchWeekStatus(); fetchRecentReports(); }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-4">
                <label className="text-sm text-gray-600">Week of:</label>
                <input
                  type="date"
                  value={weekOf}
                  onChange={(e) => setWeekOf(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#32425c]"
                />
              </div>
            </div>

            {/* Team Selection */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-[#32425c] flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Select Teams
                </h2>
                <button
                  onClick={selectAllTeams}
                  className="text-sm text-[#c41e3a] hover:text-[#a01830] font-medium"
                >
                  {selectedTeams.length === teams.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              {teams.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No teams found</p>
              ) : (
                <div className="space-y-2">
                  {teams.map(team => {
                    const status = weekStatus[team.team_id];
                    const isSent = status?.sent;
                    
                    return (
                      <label
                        key={team.team_id}
                        className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition ${
                          selectedTeams.includes(team.team_id)
                            ? 'border-[#c41e3a] bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedTeams.includes(team.team_id)}
                            onChange={() => toggleTeam(team.team_id)}
                            className="w-5 h-5 rounded border-gray-300 text-[#c41e3a] focus:ring-[#c41e3a]"
                          />
                          <div>
                            <p className="font-medium text-gray-900">{team.name}</p>
                            <p className="text-sm text-gray-500">
                              {team.player_count} players • {team.teacher_count} teachers
                            </p>
                          </div>
                        </div>
                        {isSent && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Sent
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Send Button */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    {selectedTeams.length > 0 ? (
                      <>
                        <strong>{stats.totalTeachers}</strong> teachers will receive emails for <strong>{stats.totalPlayers}</strong> players
                      </>
                    ) : (
                      'Select teams above to send reports'
                    )}
                  </p>
                </div>
                <button
                  onClick={handleSend}
                  disabled={sending || selectedTeams.length === 0}
                  className="flex items-center gap-2 px-6 py-3 bg-[#c41e3a] text-white rounded-xl font-semibold hover:bg-[#a01830] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Weekly Reports
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar - Recent Reports */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-[#32425c] flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Recent Submissions
                </h2>
                <button
                  onClick={() => setShowReports(!showReports)}
                  className="text-sm text-[#c41e3a] hover:text-[#a01830] font-medium"
                >
                  {showReports ? 'Hide' : 'View All'}
                </button>
              </div>

              {recentReports.length === 0 ? (
                <p className="text-gray-500 text-center py-6 text-sm">
                  No reports submitted for this week yet
                </p>
              ) : (
                <div className="space-y-3">
                  {recentReports.slice(0, showReports ? 20 : 5).map(report => (
                    <div
                      key={report.report_id}
                      className="p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-gray-900 text-sm">{report.player_name}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getBehaviorColor(report.behavior_score)}`}>
                          {report.behavior_score}/5
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">by {report.teacher_name}</p>
                        {getAcademicBadge(report.academic_status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-[#32425c] rounded-xl p-6 text-white">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5" />
                This Week's Stats
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Reports Submitted</span>
                  <span className="font-bold">{recentReports.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Avg Behavior Score</span>
                  <span className="font-bold">
                    {recentReports.length > 0 
                      ? (recentReports.reduce((sum, r) => sum + r.behavior_score, 0) / recentReports.length).toFixed(1)
                      : '-'
                    }
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">On Track Academically</span>
                  <span className="font-bold">
                    {recentReports.length > 0 
                      ? `${Math.round(recentReports.filter(r => r.academic_status === 'on_track').length / recentReports.length * 100)}%`
                      : '-'
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ParentLayout>
  );
}
